import { useCallback, useState } from "react"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { UpdateAssetParentDocument } from "@hydrologiq/gql-experience/graphql"

export interface UseLinkAssetReturn {
  linkAsset: (childAssetId: string, parentAssetId: string) => Promise<void>
  loading: boolean
  error: string | undefined
}

/**
 * Hook to link a child asset to a parent asset (making the parent a group).
 * Wraps the UpdateAssetParent mutation.
 */
export function useLinkAsset(): UseLinkAssetReturn {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [updateAssetParentMutation] = useMutationWithEndpoint(
    UpdateAssetParentDocument,
    undefined,
    "primary",
  )

  const linkAsset = useCallback(
    async (childAssetId: string, parentAssetId: string) => {
      setLoading(true)
      setError(undefined)
      try {
        const result = await updateAssetParentMutation({
          variables: { id: childAssetId, parent: parentAssetId },
        })
        const { errors } = result
        if (errors?.length) {
          throw new Error(errors[0].message)
        }
      } catch (err: any) {
        const message =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to link asset"
        setError(message)
        throw new Error(message)
      } finally {
        setLoading(false)
      }
    },
    [updateAssetParentMutation],
  )

  return { linkAsset, loading, error }
}
